import { useAppSelector } from "./useAppSelector";
import { useSelector } from "react-redux";
import { TCartItem, TGeneralAges } from "../store/types";
import { agesSelector } from "../selectors/Selectors";
import {
    getNewOrModifiedCartProducts,
    hasBookstat,
    isAccommodation,
    setAdditionalOnBookedAccommodationProductExtras,
    isCancelled,
    isBooked,
} from "../BusinessUtils";
import { getCartItemPrice } from "../Helper";

interface CartPriceCalculation {
    totalPrice: number;
    totalAdditionalPrice: number;
    newOrModifiedCartProducts: TCartItem[];
    nonCancelledCartProducts: TCartItem[];
}

export const calculatePriceWithBookedChanges = (item: TCartItem, generalAges: TGeneralAges): number => {
    const basePrice = !isBooked(item) ? getCartItemPrice(item, generalAges) : item.bookedprice || 0;

    // Calculate additional price if there are new guests added to a booked product
    const guestsAddedPrice =
        item.bookedprice &&
        item.bookedguestages &&
        Object.values(item.bookedguestages).length < Object.values(item.guestages).length
            ? item.price - item.bookedprice
            : 0;

    return basePrice + guestsAddedPrice;
};

export const useCartPriceCalculation = (): CartPriceCalculation => {
    const generalAges: TGeneralAges = useSelector(agesSelector);

    const nonCancelledCartProducts = (useAppSelector(state => state.checkoutCart?.products) || [])
        .filter((product: TCartItem) => !isCancelled(product))
        .map((product: TCartItem) => setAdditionalOnBookedAccommodationProductExtras(product));

    // Get all new or modified products (already filtered for non-cancelled)
    const newOrModifiedCartProducts = getNewOrModifiedCartProducts(nonCancelledCartProducts);
    const newOrModifiedAccommodationCartProducts = newOrModifiedCartProducts.filter(isAccommodation);

    let totalPrice = 0;
    let totalAdditionalPrice = 0;

    // Calculate total price for all non-cancelled products, with booked changes.
    nonCancelledCartProducts.forEach((item: TCartItem) => {
        const itemPrice = calculatePriceWithBookedChanges(item, generalAges);
        totalPrice += itemPrice;
    });

    // Calculate additional price for new or modified products
    newOrModifiedCartProducts.forEach((item: TCartItem) => {
        if (isBooked(item)) {
            const priceDifference = item.bookedprice ? item.price - item.bookedprice : 0;
            totalAdditionalPrice += priceDifference;
        } else {
            totalAdditionalPrice += item.price * (item.count || 1);
        }
    });

    // Handle accommodation extras
    const accommodationExtras = newOrModifiedAccommodationCartProducts
        .flatMap((accommodation: TCartItem) =>
            (accommodation.info?.extras || []).filter((extra: TCartItem) => !isCancelled(extra))
        ) // Filter out cancelled extras
        .filter((extra: TCartItem) => !hasBookstat(extra) || extra.additional)
        .filter((extra: TCartItem) => {
            // Filter out mandatory web extras and check quantity rules
            if (extra.rules.mandatory_web) {
                return false;
            }
            return (extra.quantity !== 0 && extra.rules.max_qty !== 0) || extra.checked === true;
        });

    // Calculate prices for accommodation extras
    accommodationExtras.forEach((item: TCartItem) => {
        const extraPrice = item.additional?.price || item.price;
        const itemPrice = calculatePriceWithBookedChanges(item, generalAges);

        totalPrice += itemPrice;
        totalAdditionalPrice += extraPrice;
    });

    return {
        totalPrice,
        totalAdditionalPrice,
        newOrModifiedCartProducts,
        nonCancelledCartProducts,
    };
};
